import React, { useEffect, useMemo, useState } from 'react';
import { getProductLabelCategoryStats, LabelSource, ProductLabelCategoryRow } from '../../api/assets';
import { targetText } from './productLabels';

// 品类审核进展 (2026-10-09).
//
// 这一页只回答一个问题: **哪些采集类目可以放行自动打标。**
//
// 判据是逐品类的修正率, 所以统计也必须逐品类 —— 全局一个数会把差品类藏在好品类后面。
// 2026-10-08 实测过这种藏法的厉害: 整体剔除率 50% 看着平平无奇, 拆开才发现 watch
// 一个品类占了几乎全部, 其余品类都在 0~16%。
//
// **表里有两套口径, 分两组列摆开**(2026-10-10):
//   全量   这个品类一共多少要审、被剔了多少 —— 看工作量
//   样本内 本轮抽审的进度与修正率         —— 看能不能放行
// 混在一起正是之前没人看得懂的原因: 「已审 2,114」是全量、「待审 310」是样本内。

/** 放行线: 修正率 ≤5%。**比目标严一档是刻意的** —— 审核员看得见 AI 答案,
 *  测到的是"主动纠错率", 它只是真实错误率的**下界**(漏看的不计入)。
 *  所以这个数适合用来刷掉品类, 不适合用来放行品类; 用严格度换测量偏差。 */
const PASS_RATE = 0.05;

/** 95% 置信上界(Wilson 区间)。
 *
 *  **放行判据用的是上界而不是观测值。** 观测到 2/50 = 4% 看着达标, 但 50 条的样本
 *  撑不起这个结论 —— 真实值有可能是 12%。上界把"样本够不够"这件事直接算进了判据里,
 *  于是不需要再单独定一个"至少多少条"的阈值: 样本不够时上界自然压不下来。
 *
 *  用 Wilson 而不是 Clopper-Pearson: 闭式解, 前端不用引数值库, 两者在这个量级上
 *  差不到 0.1 个百分点(n=300/k=7 实算: Wilson 4.73% vs CP 4.68%)。 */
function wilsonUpper(k: number, n: number): number {
    if (n <= 0) return 1;
    const z = 1.96, p = k / n;
    const d = 1 + (z * z) / n;
    return (p + (z * z) / (2 * n) + z * Math.sqrt((p * (1 - p)) / n + (z * z) / (4 * n * n))) / d;
}

type Verdict = { text: string; cls: string; title: string };

/** 结论。**分母是「可用判断」= 确认 + 修正, 不含「判不了」。**
 *
 * 「判不了」是终态、不进下游, 对放行决策零贡献 —— 但它很占样本: 2026-10-10 实测
 * dev 的 earphone 已审 1,371 条里 1,175 条(85.7%)是判不了, 可用判断只有 196 条。
 * 所以"审满 300 条"这个门槛是假的, 真正要数够的是可用判断。
 *
 * 判据用 95% 置信上界, 于是"要不要加样本量"由数字自己回答:
 *   观测值就超线   → 加样本也没用, 直接不可放行
 *   观测值达标但上界没压下来 → 样本不够, 继续审就有用
 *   上界达标       → 可放行 */
function verdictOf(r: ProductLabelCategoryRow): Verdict {
    const usable = r.confirmed + r.corrected;
    // **「没打标」和「打了但全被剔了」要分开**(2026-10-09): 两者 reviewable 都是 0, 但
    // 一个是"还没开工"、另一个是"开工了但这个品类的图根本认不出主加工面" —— 前者加
    // 打标量就有结果, 后者加多少都没用。合成一句「无可审条目」会把 hat 这种 22,471 张
    // 一张没打的品类, 和 watch 那次 100% 被剔的事故, 显示成同一件事。
    if (r.reviewable === 0 && r.excludedAll === 0) {
        return { text: '未开始打标', cls: 'bg-slate-100 text-slate-400',
            title: r.cleanImages > 0 ? `这个品类有 ${r.cleanImages.toLocaleString()} 张图可以打标，但一张都还没打`
                : '这个品类没有清洗通过的图' };
    }
    if (r.reviewable === 0) {
        return { text: '全部被剔除', cls: 'bg-rose-50 text-rose-600',
            title: `打了 ${r.excludedAll.toLocaleString()} 条，全部判为「主加工面认不出」—— 没有一条进审核面。`
                + `2026-10 的 watch 事故就是这样（一句 prompt 导致整个品类被剔），先查打标 prompt。` };
    }
    if (usable === 0) {
        return { text: '尚无可用判断', cls: 'bg-slate-100 text-slate-500',
            title: '样本里还没有人给出「判对」或「改一下」的结论（只有待审或判不了）' };
    }
    const rate = r.corrected / usable;
    const upper = wilsonUpper(r.corrected, usable);
    const fmt = `观测修正率 ${(100 * rate).toFixed(1)}%（${r.corrected}/${usable}），95% 置信上界 ${(100 * upper).toFixed(1)}%`;
    if (rate > PASS_RATE) {
        return { text: '不可放行', cls: 'bg-rose-50 text-rose-600',
            title: `${fmt}。观测值本身已超过放行线 ${PASS_RATE * 100}%，加样本量也救不回来。` };
    }
    if (upper > PASS_RATE) {
        return { text: `样本不够 (${usable})`, cls: 'bg-amber-50 text-amber-700',
            title: `${fmt}。观测值达标但样本撑不起结论，继续审**可用判断**到上界压到 ${PASS_RATE * 100}% 以下即可放行。`
                + `注意「判不了」不算可用判断。` };
    }
    return { text: '可放行', cls: 'bg-emerald-50 text-emerald-700', title: `${fmt} ≤ ${PASS_RATE * 100}%` };
}

const pct = (a: number, b: number) => (b > 0 ? `${((100 * a) / b).toFixed(1)}%` : '—');

export default function ProductLabelCategoryProgress(
    { source, sampleSeed, sampleSize }: { source: LabelSource; sampleSeed: string; sampleSize: number },
) {
    const [rows, setRows] = useState<ProductLabelCategoryRow[] | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        let alive = true;
        setRows(null); setError('');
        getProductLabelCategoryStats(source, { sampleSeed, sampleSize })
            .then((r) => { if (alive) setRows(r.list || []); })
            .catch((e) => { if (alive) setError(String(e)); });
        return () => { alive = false; };
    }, [source, sampleSeed, sampleSize]);

    // 先按还要审多少排(这一页原本的用途: "先审哪个品类", 剩得多的优先), **再按可打标图数**。
    // 第二个键是 2026-10-09 加的: 这一页现在也列没开过工的品类, 它们 pending 全是 0,
    // 只有一个键的话这 35 个品类会按后端返回顺序乱排 —— 而其中 hat 有 22,471 张图、
    // wallet 13,585 张, 恰恰是最该先排期打标的。
    const sorted = useMemo(
        () => [...(rows || [])].sort((a, b) => b.pending - a.pending || b.cleanImages - a.cleanImages),
        [rows]);

    if (error) return <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">错误: {error}</div>;
    if (!rows) return <div className="card p-6 text-sm text-slate-400">加载中…</div>;

    const th = 'text-right px-3 py-2 font-medium';
    return (
        <div className="card p-0 overflow-x-auto">
            <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 bg-slate-50">
                    <tr>
                        <th className="text-left px-3 py-2 font-medium" rowSpan={2}>采集类目</th>
                        <th className={th} colSpan={5}>打标进度（全量）</th>
                        <th className={`${th} border-l border-slate-200`} colSpan={6}>本轮抽审样本</th>
                        <th className="text-left px-3 py-2 font-medium" rowSpan={2}>结论</th>
                    </tr>
                    <tr>
                        <th className={th} title="清洗后可以拿去打标的商品图数 —— 这个品类真实的盘子。口径与打标队列的准入逐字一致：清洗通过 + 下载成功 + 有 url">可打标图</th>
                        <th className={th} title="打标成功的条数，含被剔除的（剔除也是一次打标的结论）">已打标</th>
                        <th className={th} title="已打标 ÷ 可打标图。这一列回答「这个品类还要不要继续打」">进度</th>
                        <th className={th} title="主加工面在图上认不出来，不进审核面。可审 = 已打标 − 已剔除">已剔除</th>
                        <th className={th} title="这个品类全量里人工已经审过多少条（确认 + 修正 + 判不了）。注意这是全量，不是样本内 —— 有些品类已经整类普查完了">人工已审</th>
                        <th className={`${th} border-l border-slate-200`} title="本轮抽出来要审的条数 = min(300, 可审总数)">样本量</th>
                        <th className={th}>待审</th>
                        <th className={th} title="人看了也判不出材质。终态，不进下游，对放行决策没有贡献 —— 但它占样本名额">判不了</th>
                        <th className={th} title="确认 + 修正。放行判据的分母，不含「判不了」">可用判断</th>
                        <th className={th} title="修正 ÷ 可用判断。这是真实错误率的下界，不是准确率">修正率</th>
                        <th className={th} title="修正率的 95% 置信上界。它 ≤ 放行线才算达标 —— 这一列同时回答了「样本够不够」">上界</th>
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((r) => {
                        // **可用判断不含「判不了」**: 那是终态、不进下游, 对放行零贡献。
                        const usable = r.confirmed + r.corrected;
                        const goal = Math.min(sampleSize, r.reviewable);
                        // 已打标**含被剔除的**: 剔除也是打标跑过一遍的结论, 不算就会显示成
                        // "还没打", 而 watch 这种剔除率高的品类会凭空少掉一大截进度。
                        const labeled = r.reviewable + r.excludedAll;
                        // **全量**人工审核量 = 确认 + 修正 + 判不了。与样本内那组数无关。
                        const reviewedAll = r.reviewedAll + r.disputedAll;
                        const v = verdictOf(r);
                        return (
                            <tr key={r.target || '(未分类)'} className="border-t border-slate-100">
                                <td className="px-3 py-2 font-medium text-slate-700">
                                    {r.target ? targetText(r.target) : <span className="text-slate-400">(未分类)</span>}
                                    {r.target && <span className="ml-1.5 text-slate-400 font-mono font-normal text-xs">{r.target}</span>}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums text-slate-500">{r.cleanImages.toLocaleString()}</td>
                                <td className="px-3 py-2 text-right tabular-nums font-medium">{labeled.toLocaleString()}</td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                    {/* 没打过的标成 0%(而不是「—」): 「—」会被读成"没有这个数",
                                        而这里恰恰有一个很确定的数 —— 一张都没打。 */}
                                    <span className={labeled >= r.cleanImages && r.cleanImages > 0 ? 'text-emerald-600 font-medium'
                                        : labeled === 0 ? 'text-slate-300' : 'text-amber-600'}>
                                        {pct(labeled, r.cleanImages)}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums text-rose-500">{r.excludedAll.toLocaleString()}</td>
                                <td className="px-3 py-2 text-right tabular-nums">
                                    {/* 全量人工审核量。**和右边那组样本内的数不是一回事** ——
                                        实测 earphone 这里是 4,444(整类普查完了), 样本内只有三百。
                                        拆出「可用 / 判不了」是因为这一页上「已审核」一直特指确认+修正,
                                        而判不了是单独一档; 只给一个合计数会和那个说法打架。 */}
                                    {reviewedAll > 0 ? (
                                        <>
                                            <span className="font-medium">{reviewedAll.toLocaleString()}</span>
                                            <span className="ml-1 text-[11px] text-slate-400 font-normal">
                                                {r.reviewedAll}+{r.disputedAll}
                                            </span>
                                            {r.reviewable > 0 && (
                                                <div className="text-[11px] text-slate-400">{pct(reviewedAll, r.reviewable)}</div>
                                            )}
                                        </>
                                    ) : <span className="text-slate-300">0</span>}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums border-l border-slate-100">
                                    {/* 样本量与目标不等时标出来: 该品类可审的总共就这么多 */}
                                    {r.sampleN.toLocaleString()}
                                    {goal < sampleSize && <span className="ml-1 text-[11px] text-slate-400">(不足 {sampleSize})</span>}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">{r.pending.toLocaleString()}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-amber-600">
                                    {r.disputed.toLocaleString()}
                                    {r.disputed > 0 && <span className="ml-1 text-[11px] text-slate-400">{pct(r.disputed, r.sampleN - r.pending)}</span>}
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums font-medium">
                                    {usable.toLocaleString()}
                                    <span className="ml-1 text-[11px] text-slate-400 font-normal">
                                        {r.confirmed}+{r.corrected}
                                    </span>
                                </td>
                                <td className="px-3 py-2 text-right tabular-nums">{pct(r.corrected, usable)}</td>
                                <td className="px-3 py-2 text-right tabular-nums font-medium">
                                    {usable > 0 ? `${(100 * wilsonUpper(r.corrected, usable)).toFixed(1)}%` : '—'}
                                </td>
                                <td className="px-3 py-2">
                                    <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium whitespace-nowrap ${v.cls}`} title={v.title}>{v.text}</span>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {/* 口径摆在页面上, 不能只写在代码注释里 —— 看这张表的人要据它做放行决定。 */}
            <div className="px-3 py-2 text-xs text-slate-500 border-t border-slate-100 leading-relaxed">
                <b>可打标图</b> = 清洗通过 + 下载成功 + 有 url 的商品图，<b>与打标队列的准入口径逐字一致</b> ——
                它是这个品类真实的盘子。<b>已打标</b>含被剔除的（剔除也是一次打标的结论），
                <b>进度</b> = 已打标 ÷ 可打标图，回答「这个品类还要不要继续打」。
                <br />
                <b>人工已审</b>是<b>全量</b>口径（确认+修正 / 判不了，下方百分比是占可审的比例），
                <b>与右边那组样本内的数不是一回事</b> —— 抽样只是派活的机制，人实际审过的往往远多于样本。
                <br />
                <b>样本量</b> = min({sampleSize}, 可审总数)，每个品类各抽各的；可审 = 已打标 − 已剔除。
                <b>待审 / 确认 / 修正</b> 都只统计样本内 —— 与「人工抽检」页看到的是同一批行。
                <br />
                <b>「判不了」不计入可用判断</b> —— 它是终态、不进下游，对放行决策零贡献，但一样占样本名额。
                所以门槛不是「审满 {sampleSize} 条」，而是<b>可用判断要够</b>。
                <br />
                <b>修正率</b> = 修正 ÷ 可用判断；<b>上界</b>是它的 95% 置信上界。
                <b>上界 ≤ {PASS_RATE * 100}% 才判「可放行」</b> —— 用上界而不是观测值，是因为
                2/50 = 4% 看着达标但撑不起结论（真实值可能到 12%）。这一列同时回答了「要不要加样本量」：
                观测值就超线 = 加样本也没用；观测值达标但上界没压下来 = 继续审就有用。
                <br />
                ⚠️ 审核员看得见 AI 的答案，所以这个数是<b>真实错误率的下界</b>（漏看的不计入）——
                <b>判不通过是可靠的，判通过是偏乐观的</b>。放行时请按比目标更严的标准卡。
            </div>
        </div>
    );
}
